import type {
  AiInsightComparisonFacts,
  AiInsightOption,
  AiInsightOptionFacts,
  AmortizationSystem
} from "./contracts.js";

interface FinancialFactSource {
  annualRate: number;
  financedAmount: number;
  firstPayment: number;
  lastPayment: number;
  totalInterest: number;
  totalInsurance: number;
  totalAdminFees: number;
  upfrontFee: number;
  totalPaid: number;
  estimatedCetAnnual: number | null;
}

interface FinancialFingerprintSource extends FinancialFactSource {
  bankId: string;
  system: AmortizationSystem;
  roundingAdjustment: number;
}

function roundFact(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function shareOfTotal(value: number, totalPaid: number): number {
  return totalPaid > 0 ? roundFact((value / totalPaid) * 100) : 0;
}

export function deriveOptionFacts(option: FinancialFactSource): AiInsightOptionFacts {
  const costOverPrincipal =
    option.financedAmount > 0
      ? ((option.totalPaid - option.financedAmount) / option.financedAmount) * 100
      : 0;
  const paymentReduction =
    option.firstPayment > 0
      ? ((option.firstPayment - option.lastPayment) / option.firstPayment) * 100
      : 0;

  return {
    principalSharePct: shareOfTotal(option.financedAmount, option.totalPaid),
    interestSharePct: shareOfTotal(option.totalInterest, option.totalPaid),
    insuranceSharePct: shareOfTotal(option.totalInsurance, option.totalPaid),
    adminFeesSharePct: shareOfTotal(option.totalAdminFees, option.totalPaid),
    upfrontFeeSharePct: shareOfTotal(option.upfrontFee, option.totalPaid),
    costOverPrincipalPct: roundFact(costOverPrincipal),
    cetSpreadPp:
      option.estimatedCetAnnual === null
        ? null
        : roundFact(option.estimatedCetAnnual - option.annualRate),
    firstToLastPaymentDiff: roundFact(option.firstPayment - option.lastPayment),
    paymentReductionPct: roundFact(paymentReduction)
  };
}

export function calculationFingerprint(option: FinancialFingerprintSource): string {
  const stable = [
    option.bankId,
    option.system,
    option.annualRate,
    option.financedAmount,
    option.firstPayment,
    option.lastPayment,
    option.totalInterest,
    option.totalInsurance,
    option.totalAdminFees,
    option.upfrontFee,
    option.roundingAdjustment,
    option.totalPaid,
    option.estimatedCetAnnual ?? "null"
  ]
    .map((value) => (typeof value === "number" ? value.toFixed(6) : value))
    .join("|");
  let hash = 0x811c9dc5;
  for (let index = 0; index < stable.length; index += 1) {
    hash ^= stable.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function optionKey(option: {
  bankId: string;
  system: AmortizationSystem;
}): string {
  return `${option.bankId}-${option.system}`;
}

export function deriveComparisonFacts(
  selected: AiInsightOption,
  options: AiInsightOption[]
): AiInsightComparisonFacts {
  const bestEligible = options
    .filter((option) => option.eligible)
    .sort((left, right) => left.totalPaid - right.totalPaid)[0];

  if (!bestEligible) {
    return { status: "no-eligible-reference" };
  }

  if (!selected.eligible) {
    return {
      status: "ineligible",
      referenceBankShortName: bestEligible.bankShortName,
      referenceSystem: bestEligible.system,
      totalPaidDelta: roundFact(selected.totalPaid - bestEligible.totalPaid),
      firstPaymentDelta: roundFact(selected.firstPayment - bestEligible.firstPayment)
    };
  }

  if (optionKey(selected) === optionKey(bestEligible)) {
    return {
      status: "best-eligible",
      referenceBankShortName: bestEligible.bankShortName,
      referenceSystem: bestEligible.system
    };
  }

  return {
    status: "eligible-higher-cost",
    referenceBankShortName: bestEligible.bankShortName,
    referenceSystem: bestEligible.system,
    totalPaidDelta: roundFact(selected.totalPaid - bestEligible.totalPaid),
    firstPaymentDelta: roundFact(selected.firstPayment - bestEligible.firstPayment)
  };
}
